<template>
  <div>
    <ValidationObserver
      ref="observer"
      v-slot="{ invalid, dirty }"
    >
      <v-row
        align="center"
        justify="center"
      >
        <v-col>
          <VTextField
            v-model="data.code"
            outlined
            :disabled="!isEditable"
            :label="$t('forms.labels.code')"
            :rules="{ required: true, min: 1, max: 200 }"
            data-cy="addProduct-code"
          />
        </v-col>
        <v-col>
          <VTextField
            v-model="data.pesticideNumber"
            outlined
            :disabled="!isEditable"
            :label="$t('forms.labels.pesticideNumber')"
            :rules="{ min: 1, max: 200 }"
          />
        </v-col>
      </v-row>
      <v-row
        align="center"
        justify="center"
      >
        <v-col>
          <VTextField
            v-model="data.name"
            outlined
            :disabled="!isEditable"
            :label="$t('forms.labels.name')"
            :rules="{ required: true, min: 1, max: 200 }"
            data-cy="addProduct-name"
          />
        </v-col>
      </v-row>

      <v-row
        align="center"
        justify="center"
      >
        <v-col>
          <VTextField
            v-model="data.relativeDensity"
            type="number"
            outlined
            :disabled="!isEditable"
            :label="$t('forms.labels.relativeDensity')"
            :rules="{ decimal: 2, required: true, min_value: 0.1}"
            data-cy="product-catalog-relativeDensity"
          />
        </v-col>
        <v-col>
          <VTextField
            v-model="data.activeConcentration"
            type="number"
            outlined
            suffix="%"
            :disabled="!isEditable"
            :label="$t('forms.labels.activeConcentration')"
            :rules="{ decimal: 2, min_value:0, max_value:100}"
            data-cy="product-catalog-activeConcentration"
          />
        </v-col>
      </v-row>

      <v-row
        align="center"
        justify="center"
      >
        <v-col>
          <VTextarea
            v-model="data.description[selectedLang]"
            :disabled="!isEditable"
            :label="$t(`forms.labels.description_${selectedLang}`)"
            :rules="{ required: false, max: 1000 }"
          />
        </v-col>
      </v-row>

      <v-row
        v-for="(field, index) in fileFieldsTodisplay"
        :key="index"
        align="center"
        justify="center"
      >
        <v-col>
          <VFile
            v-model="data[`${field}_${selectedLang}`]"
            :disabled="!isEditable"
            :isEdition="isEdition"
            :label="$t(`forms.labels.${field}_${selectedLang}`)"
            :rules="validationFileRules"
            :showSize="!isEdition"
            :cyData="`${field}_${selectedLang}`"
            appendOuterIcon="mdi-eye"
          />
        </v-col>
      </v-row>
      <SlotScopeWatcher
        :value="dirty"
        @change="$emit('dirtyChanged', dirty)"
      />
      <SlotScopeWatcher
        :value="invalid"
        @change="$emit('validChanged', !invalid)"
      />
    </ValidationObserver>
  </div>
</template>

<script>
import { ValidationObserver } from 'vee-validate';
import SlotScopeWatcher from '@/components/shared/widgets/forms/validationHelper/SlotScopeWatcher';
import VFile from '@/components/shared/widgets/forms/VuetifyInputs/VFile';
import VTextarea from '@/components/shared/widgets/forms/VuetifyInputs/VTextarea.vue';
import VTextField from '@/components/shared/widgets/forms/VuetifyInputs/VTextField.vue';
import { mapGetters } from 'vuex';
import FormFilesMixin from '@/mixins/FormFilesMixin';

export default {
    components: {
        SlotScopeWatcher,
        ValidationObserver,
        VFile,
        VTextarea,
        VTextField,
    },
    mixins: [
        FormFilesMixin,
    ],
    props: {
        isEditable: {
            type: Boolean,
            default: true,
        },
        populateWith: {
            type: Object,
            default: () => ({ empty: true }),
        },
    },
    data() {
        return {
            fileFields: [
                'msds',
                'report',
                'procedure',
            ],
            fileFieldsTodisplay: [
                'msds',
                'report',
                'procedure',
            ],
            data: {
                name: '',
                relativeDensity: 1,
                code: '',
                pesticideNumber: '',
                description: {},
                files: {
                    msds: {},
                    report: {},
                    procedure: {},
                },
            },
            validationFileRules: {
                mimes: ['application/pdf'],
                size: 50 * 1024,
            },
        };
    },
    computed: {
        ...mapGetters([
            'teamMainLang',
            'selectedLang',
            'lang',
            'allTeamLangs',
        ]),
        /**
         * Chunk by two for the template
         * @return {Array}
         */
        displayedFileFields() {
            return this.fileFields.reduce((output, item, index) => {
                const result = [...output];
                const chunkIndex = Math.floor(index / 2);

                if (!result[chunkIndex]) {
                    result[chunkIndex] = [];
                }

                result[chunkIndex].push(item);

                return result;
            }, []);
        },
        /**
         * @return {Boolean}
         */
        isEdition() {
            return !this.populateWith.empty;
        },
    },
    watch: {
        data: {
            handler() {
                this.updateData();
            },
            deep: true,
        },
    },
    async created() {
        if (this.isEdition) {
            const fileFieldsWithLang = [];
            this.fileFields.forEach(fieldName => {
                this.allTeamLangs.forEach(spLang => {
                    const lang = spLang.key;
                    const key = `${fieldName}_${lang}`;
                    if (this.populateWith[key]) {
                        fileFieldsWithLang.push(key);
                    }
                });
            });

            this.fileFields = fileFieldsWithLang;
            const data = await this.prepareEditionData(this.populateWith);
            this.data = data;
        }
        if (!this.data.description) {
            this.data.description = {};
        }
    },
    methods: {
        /**
         * @param {String} fieldName
         */
        updateData() {
            // When creation, no need for additional process
            if (!this.isEdition) {
                this.$emit('sendData', this.data);
                return;
            }

            // Specific treatment for the files
            const { originalFiles, ...payload } = this.data;
            this.$emit('sendData', payload);
        },
    },
};
</script>
